import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { auth } from "../config/firebase";
import { ProductRequest } from "../models/product.models";
import { FetchedFileList } from "../models/uploaded-file";
import {
  ClientRequest,
  SingleNotification,
  UserPhoneNumbers,
} from "../models/user.models";
import { Credentials } from "../pages/LoginPage";
import { User } from "../store/user.store";
import { axiosService } from "./axios.service";
import { GridSortModel } from "@mui/x-data-grid";

const BASE_URL = import.meta.env.VITE_BACKEND_URL;

export class UserService {
  static async login({
    email,
    password,
  }: Credentials): Promise<User | undefined> {
    try {
      await signInWithEmailAndPassword(auth, email, password);
      const user: User = await axiosService.get(`${BASE_URL}/user`);

      if (user) {
        return user;
      }
    } catch (error) {
      console.error(error);
    }
  }

  static async getLoggedInUser(): Promise<User | undefined> {
    try {
      return await axiosService.get(`${BASE_URL}/user`);
    } catch (error) {
      console.log(error);
    }
  }

  static async checkUserInfoValid(email: string, password: string) {
    try {
      if (!auth.currentUser) {
        throw "No logged in user";
      }
      const credential = EmailAuthProvider.credential(email, password);
      await reauthenticateWithCredential(auth.currentUser, credential);
      return true;
    } catch (error) {
      console.error(error);

      return false;
    }
  }

  static getUserContactUsRequests = async (): Promise<ClientRequest[]> => {
    const response: ClientRequest[] = await axiosService.get(`/contact`);

    console.log(response);

    return response;
  };

  static async logout() {
    try {
      await signOut(auth);
    } catch (error) {
      console.error(error);
    }
  }

  static changePassword = async (email: string, newPassword: string) => {
    try {
      const data = {
        email: email,
        newPassword: newPassword,
      };
      const response = await axiosService.post(`/user/change-password`, data);
      return response;
    } catch (error) {
      console.error(error);
    }
  };

  static getAllToolInfo = async (): Promise<ProductRequest[] | undefined> => {
    try {
      const response: ProductRequest[] = await axiosService.get(
        `/user/product-requests`
      );

      return response;
    } catch (error) {
      console.error(error);
    }
  };

  static async getUserPhoneNumbers() {
    try {
      const phoneNumbers: UserPhoneNumbers = await axiosService.get(
        `/user/phone-numbers`
      );
      return phoneNumbers;
    } catch (error) {
      console.error(error);
    }
  }

  static setNotificationSettings = async (
    userId: string,
    general: boolean,
    personal: boolean
  ) => {
    try {
      const requestData = {
        userId: userId,
        general: general,
        personal: personal,
      };

      const data = await axiosService.put(
        "/user/notification-settings",
        requestData
      );

      return data;
    } catch (error) {
      console.log(error);
      return error;
    }
  };

  static async resetPassword(email: string) {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error(error);
    }
  }

  static async getUserFileList(page: number, limit: number = 7) {
    try {
      const files: FetchedFileList = await axiosService.get(
        `/user/attachments/${page}/${limit}`
      );
      return files;
    } catch (error) {
      console.error(error);
    }
  }

  static async getUserProductRequests(
    page?: number | null,
    limit?: number | null,
    productRequestType?: string | null,
    status?: string | null,
    fetchAll?: boolean | null,
    filter?: GridSortModel
  ): Promise<ProductRequest | undefined> {
    let urlExtention = "";
    if (filter?.[0].field) {
      const model = filter[0];
      urlExtention += `&filterField=${model.field}&filterSort=${model.sort}`;
    }
    const response: ProductRequest = await axiosService.get(
      `/user/product-requests?page=${page}&limit=${limit}&type=${productRequestType}&fetchAll=${fetchAll}&status=${status}${urlExtention}`
    );

    return response;
  }

  static async markNotificationsAsRead(userId: string) {
    await axiosService.put(`/notification/read-all-notifications`, { userId });
  }

  static async fetchNotifications(): Promise<SingleNotification[] | undefined> {
    try {
      const response: SingleNotification[] = await axiosService.get(
        `/notification`
      );
      return response;
    } catch (error) {
      console.error("Error fetching notifications:", error);
      throw error;
    }
  }

  static async setInitialPassword(oobCode: string) {
    try {
      const data = {
        oobCode: oobCode,
      };
      const response: any = await axiosService.post(
        `/user/set-initialPassword`,
        data
      );
      return response;
    } catch (error) {
      console.error(error);
    }
  }
}
